import fetch from '../../../../utils/fetch'

const getInitialState = () => {
  return {
    isLoading: false,
    isLoadingMore: false,
    projects: [],
    selectedProject: null,
    error: null,
    page: 1,
    perPage: 25,
    total: 0,
    filters: {
      name: '',
      projectState: null,
      isFeatured: null,
    },
    sortField: 'updatedAt',
    sortDirection: 'desc',
    isSaving: false,
    saveError: null,
  }
}

const state = getInitialState()

const getters = {
  projects(state) {
    return state.projects
  },
  selectedProject(state) {
    return state.selectedProject
  },
  isLoading(state) {
    return state.isLoading
  },
  isLoadingMore(state) {
    return state.isLoadingMore
  },
  isSaving(state) {
    return state.isSaving
  },
  error(state) {
    return state.error
  },
  saveError(state) {
    return state.saveError
  },
  page(state) {
    return state.page
  },
  perPage(state) {
    return state.perPage
  },
  total(state) {
    return state.total
  },
  totalPages(state) {
    return Math.ceil(state.total / state.perPage)
  },
  filters(state) {
    return state.filters
  },
  sortField(state) {
    return state.sortField
  },
  sortDirection(state) {
    return state.sortDirection
  },
}

const actions = {
  /**
   * @function getProjects - Fetch projects list with filters and pagination
   * @param {Object} context - Vuex context
   * @param {Object} options - Optional filter/sort/pagination overrides
   * @returns {Promise}
   */
  async getProjects({ commit, state }, options = {}) {
    commit(types.SET_LOADING)
    
    try {
      const params = new URLSearchParams({
        page: options.page || state.page,
        itemsPerPage: options.perPage || state.perPage,
      })
      
      const filters = options.filters || state.filters
      
      // Add filters
      if (filters.name) {
        params.append('name', filters.name)
      }
      if (filters.projectState !== null && filters.projectState !== undefined) {
        params.append('projectState', filters.projectState)
      }
      if (filters.isFeatured !== null && filters.isFeatured !== undefined) {
        params.append('isFeatured', filters.isFeatured)
      }
      
      // Add sorting
      const sortField = options.sortField || state.sortField
      const sortDirection = options.sortDirection || state.sortDirection
      params.append(`order[${sortField}]`, sortDirection)
      
      const response = await fetch(`/projects?${params.toString()}`)
      const data = await response.json()
      
      commit(types.SET_PROJECTS, {
        projects: data['hydra:member'] || [],
        total: data['hydra:totalItems'] || 0,
      })
      
      return data
    } catch (error) {
      commit(types.SET_ERROR, error)
      throw error
    }
  },
  
  /**
   * @function getProject - Fetch a single project by ID
   * @param {Object} context - Vuex context
   * @param {string|number} id - Project ID
   * @returns {Promise}
   */
  async getProject({ commit }, id) {
    commit(types.SET_LOADING)
    
    try {
      const response = await fetch(`/projects/${id}`)
      const data = await response.json()
      
      commit(types.SET_SELECTED_PROJECT, data)
      return data
    } catch (error) {
      commit(types.SET_ERROR, error)
      throw error
    }
  },
  
  /**
   * @function updateProject - Update project admin fields (isFeatured, projectState)
   * @param {Object} context - Vuex context
   * @param {Object} payload - { id, data }
   * @returns {Promise}
   */
  async updateProject({ commit }, { id, data }) {
    commit(types.SET_SAVING)
    
    try {
      const response = await fetch(`/admin/projects/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      })
      const updated = await response.json()
      
      commit(types.SET_SELECTED_PROJECT, updated)
      commit(types.SET_SAVE_SUCCESS)
      
      return updated
    } catch (error) {
      commit(types.SET_SAVE_ERROR, error)
      throw error
    }
  },
  
  /**
   * @function loadMoreProjects - Load next page of projects for infinite scroll
   * @param {Object} context - Vuex context
   * @returns {Promise}
   */
  async loadMoreProjects({ commit, state }) {
    const nextPage = state.page + 1
    commit(types.SET_LOADING_MORE)
    commit(types.SET_PAGE, nextPage)
    
    try {
      const params = new URLSearchParams({
        page: nextPage,
        itemsPerPage: state.perPage,
      })
      
      // Add filters
      if (state.filters.name) {
        params.append('name', state.filters.name)
      }
      if (state.filters.projectState !== null && state.filters.projectState !== undefined) {
        params.append('projectState', state.filters.projectState)
      }
      if (state.filters.isFeatured !== null && state.filters.isFeatured !== undefined) {
        params.append('isFeatured', state.filters.isFeatured)
      }
      
      // Add sorting
      params.append(`order[${state.sortField}]`, state.sortDirection)
      
      const response = await fetch(`/projects?${params.toString()}`)
      const data = await response.json()
      
      commit(types.APPEND_PROJECTS, {
        projects: data['hydra:member'] || [],
        total: data['hydra:totalItems'] || 0,
      })
      
      return data
    } catch (error) {
      commit(types.SET_ERROR, error)
      throw error
    }
  },
  
  /**
   * @function resetProjects - Reset projects list for fresh load
   * @param {Object} context - Vuex context
   */
  resetProjects({ commit }) {
    commit(types.RESET_PROJECTS)
  },
  
  /**
   * @function setPage - Set current page
   * @param {Object} context - Vuex context
   * @param {number} page - Page number
   */
  setPage({ commit }, page) {
    commit(types.SET_PAGE, page)
  },
  
  /**
   * @function setFilters - Set search filters
   * @param {Object} context - Vuex context
   * @param {Object} filters - Filter values
   */
  setFilters({ commit }, filters) {
    commit(types.SET_FILTERS, filters)
  },
  
  /**
   * @function setSort - Set sort field and direction
   * @param {Object} context - Vuex context
   * @param {Object} sort - { field, direction }
   */
  setSort({ commit }, { field, direction }) {
    commit(types.SET_SORT, { field, direction })
  },
  
  /**
   * @function clearFilters - Reset all filters
   * @param {Object} context - Vuex context
   */
  clearFilters({ commit }) {
    commit(types.CLEAR_FILTERS)
  },
  
  /**
   * @function clearSelectedProject - Clear selected project
   * @param {Object} context - Vuex context
   */
  clearSelectedProject({ commit }) {
    commit(types.CLEAR_SELECTED_PROJECT)
  },
  
  /**
   * @function clearError - Clear error state
   * @param {Object} context - Vuex context
   */
  clearError({ commit }) {
    commit(types.CLEAR_ERROR)
  },
  
  /**
   * @function clearSaveError - Clear save error state
   * @param {Object} context - Vuex context
   */
  clearSaveError({ commit }) {
    commit(types.CLEAR_SAVE_ERROR)
  },
  
  /**
   * @function refresh - Reset all state
   * @param {Object} context - Vuex context
   */
  refresh({ commit }) {
    commit(types.RESET)
  },
}

const types = {
  SET_LOADING: 'SET_LOADING',
  SET_LOADING_MORE: 'SET_LOADING_MORE',
  SET_PROJECTS: 'SET_PROJECTS',
  APPEND_PROJECTS: 'APPEND_PROJECTS',
  RESET_PROJECTS: 'RESET_PROJECTS',
  SET_SELECTED_PROJECT: 'SET_SELECTED_PROJECT',
  SET_ERROR: 'SET_ERROR',
  SET_PAGE: 'SET_PAGE',
  SET_FILTERS: 'SET_FILTERS',
  SET_SORT: 'SET_SORT',
  CLEAR_FILTERS: 'CLEAR_FILTERS',
  CLEAR_SELECTED_PROJECT: 'CLEAR_SELECTED_PROJECT',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_SAVING: 'SET_SAVING',
  SET_SAVE_SUCCESS: 'SET_SAVE_SUCCESS',
  SET_SAVE_ERROR: 'SET_SAVE_ERROR',
  CLEAR_SAVE_ERROR: 'CLEAR_SAVE_ERROR',
  RESET: 'RESET',
}

const mutations = {
  [types.SET_LOADING](state) {
    state.isLoading = true
    state.error = null
  },
  [types.SET_LOADING_MORE](state) {
    state.isLoadingMore = true
    state.error = null
  },
  [types.SET_PROJECTS](state, { projects, total }) {
    state.projects = projects
    state.total = total
    state.isLoading = false
    state.isLoadingMore = false
  },
  [types.APPEND_PROJECTS](state, { projects, total }) {
    state.projects = [...state.projects, ...projects]
    state.total = total
    state.isLoadingMore = false
  },
  [types.RESET_PROJECTS](state) {
    state.projects = []
    state.page = 1
    state.total = 0
  },
  [types.SET_SELECTED_PROJECT](state, project) {
    state.selectedProject = project
    state.isLoading = false
  },
  [types.SET_ERROR](state, error) {
    state.error = error
    state.isLoading = false
  },
  [types.SET_PAGE](state, page) {
    state.page = page
  },
  [types.SET_FILTERS](state, filters) {
    state.filters = { ...state.filters, ...filters }
  },
  [types.SET_SORT](state, { field, direction }) {
    state.sortField = field
    state.sortDirection = direction
  },
  [types.CLEAR_FILTERS](state) {
    state.filters = {
      name: '',
      projectState: null,
      isFeatured: null,
    }
    state.sortField = 'updatedAt'
    state.sortDirection = 'desc'
    state.page = 1
  },
  [types.CLEAR_SELECTED_PROJECT](state) {
    state.selectedProject = null
  },
  [types.CLEAR_ERROR](state) {
    state.error = null
  },
  [types.SET_SAVING](state) {
    state.isSaving = true
    state.saveError = null
  },
  [types.SET_SAVE_SUCCESS](state) {
    state.isSaving = false
    state.saveError = null
  },
  [types.SET_SAVE_ERROR](state, error) {
    state.isSaving = false
    state.saveError = error
  },
  [types.CLEAR_SAVE_ERROR](state) {
    state.saveError = null
  },
  [types.RESET](state) {
    Object.assign(state, getInitialState())
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
